import { Component, input, output, signal, effect } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NavigationService } from '../../../../services/navigation.service';
import { HtmlDescriptionFieldComponent } from '../../../fields/standard/html-description-field/html-description-field.component';
import { NameFieldComponent } from '../../../fields/standard/name-field/name-field.component';
import { TextFieldComponent } from '../../../fields/standard/text-field/text-field.component';
import { Client, Course, CourseText, Engines, Product, TextUtils, _ } from '@mira-avant/mtlibs';
import { AppModule } from '../../../../modules/app.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ThirdPartyModule } from '../../../../modules/third-party.module';
import { AngularModule } from '../../../../modules/angular.module';
import { TextareaFieldComponent } from '../../../fields/standard/textarea-field/textarea-field.component';
import { SectionEditorComponent } from '../../../ui/admin/section-editor/section-editor.component';
import { FormTitleComponent } from '../../../ui/forms/form-title/form-title.component';
import { LocationService } from '../../../../services/location.service';
import { firstValueFrom } from 'rxjs';
import { RubricDropdownFieldComponent } from '../../../fields/standard/rubric-dropdown-field/rubric-dropdown-field.component';
import { AdminService } from '../../../../services/admin.service';
import { ProductDropdownFieldComponent } from '../../../fields/standard/product-dropdown-field/product-dropdown-field.component';
import { ProductService } from '../../../../services/product.service';

@Component({
	selector: 'app-course-form',
	standalone: true,
	imports: [
		AppModule,
		ZorroModule,
		ThirdPartyModule,
		AngularModule,
		NameFieldComponent,
		HtmlDescriptionFieldComponent,
		TextFieldComponent,
		TextareaFieldComponent,
		SectionEditorComponent,
		FormTitleComponent,
		RubricDropdownFieldComponent,
		ProductDropdownFieldComponent,
	],
	templateUrl: './course-form.component.html',
	styleUrl: './course-form.component.scss',
})
export class CourseFormComponent {
	readonly course = input<Course | undefined>();
	readonly editLanguage = input<string | undefined>();

	readonly onSubmit = output<any>();

	readonly hasLocations = signal<boolean>(false);
	readonly engineName = signal<Engines | undefined>(undefined);

	public form: FormGroup = this.fb.group({
		name: ['', NameFieldComponent.validators],
		descriptionHtml: ['', TextFieldComponent.validators],
		descriptionShort: ['', HtmlDescriptionFieldComponent.validators],
		dataJson: ['', []],
		productName: [null, [Validators.required]],
		rubricCode: ['', []],
		ssoCourseId: [''],
	});

	public readonly Engines = Engines;

	constructor(
		private fb: FormBuilder,
		public nav: NavigationService,
		public locationService: LocationService,
		private adminService: AdminService,
		private productService: ProductService
	) {
		// Watch matrix params → load client → check locations
		this.nav.matrixParams$.subscribe(async () => {
			const client = await firstValueFrom(this.adminService.selectedClient$);

			if (!client?.uuid) {
				this.hasLocations.set(false);
				return;
			}

			const has = await this.locationService.hasLocations(client.uuid);
			this.hasLocations.set(has);
		});

		// React to course + language changes
		effect(() => {
			const currentCourse = this.course();
			const currentLang = this.editLanguage();

			if (!currentCourse || !currentLang) {
				this.form.reset();
				return;
			}

			const courseText = currentCourse.course_text?.find(
				(text: CourseText) => text.default_lang_id === currentLang
			);

			this.form.patchValue({
				name: courseText?.name || '',
				descriptionHtml: courseText?.description_html ?? null,
				descriptionShort: courseText?.description_short ?? null,
				dataJson: currentCourse.data_json ? JSON.stringify(currentCourse.data_json) : '',
				productName: currentCourse.product_name || '',
				rubricCode: currentCourse.rubric_code || '',
				ssoCourseId: currentCourse.sso_course_id || '',
			});

			// Also update engine name if product is already set
			if (currentCourse.product_name) {
				this.productChanged(currentCourse.product_name);
			}
		});
	}

	public submit(): void {
		const value = this.form.getRawValue();

		this.onSubmit.emit({
			...value,
			descriptionShort: TextUtils.toOptionalText(value.descriptionShort),
			descriptionHtml: TextUtils.toOptionalText(value.descriptionHtml),
		});
	}

	public clear(): void {
		this.form.reset();
	}

	public async productChanged(newProductName: unknown): Promise<void> {
		if (!newProductName) {
			this.engineName.set(undefined);
			return;
		}

		try {
			const product = await this.productService.getProductByName$(newProductName as string);
			this.engineName.set(product?.engine_name as Engines);
		} catch (err) {
			console.error('Failed to load product info', err);
			this.engineName.set(undefined);
		}
	}
}
